import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, Users, Play, Clock, ArrowLeft, Loader2, 
  CheckCircle, Sparkles, Zap, Target, BookOpen
} from 'lucide-react';
import BrandHeader from '../components/BrandHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LobbyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [eventCode, setEventCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(false);
  const [participantInfo, setParticipantInfo] = useState(null);
  const [lobbyStatus, setLobbyStatus] = useState(null);
  const [competitionStatus, setCompetitionStatus] = useState('lobby');
  const [mockQuizStarted, setMockQuizStarted] = useState(false);
  const [mockQuizCompleted, setMockQuizCompleted] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  // Pre-Round-1 grace countdown (60s by default — shown as a big visible timer)
  const [graceSecondsLeft, setGraceSecondsLeft] = useState(null);
  const [graceLabel, setGraceLabel] = useState('Round 1 starts in');
  const [gracePaused, setGracePaused] = useState(false);
  const lastBeepRef = useRef(null);

  // Check for event code in URL or sessionStorage, then auto-rejoin if possible
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    const codeFromSession = sessionStorage.getItem('event_code');
    const code = codeFromUrl || codeFromSession;
    if (code) {
      setEventCode(code);
    }
    if (code && !joined) {
      // Silent auto-rejoin
      (async () => {
        try {
          const response = await axios.post(`${API_URL}/api/competition/join-lobby`, {
            event_code: code.toUpperCase()
          });
          setJoined(true);
          setParticipantInfo(response.data);
          sessionStorage.setItem('participant_id', response.data.participant_id);
          sessionStorage.setItem('participant_name', response.data.child_name);
          sessionStorage.setItem('event_code', code.toUpperCase());
        } catch (err) {
          // If auto-rejoin fails (e.g. event closed), clear stale session and fall back to manual entry
          sessionStorage.removeItem('event_code');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll lobby status when joined
  useEffect(() => {
    let interval;
    if (joined) {
      const pollStatus = async () => {
        try {
          const [lobbyRes, statusRes] = await Promise.all([
            axios.get(`${API_URL}/api/competition/lobby-status`),
            axios.get(`${API_URL}/api/competition/status`)
          ]);
          setLobbyStatus(lobbyRes.data);
          
          // Track announcements
          if (statusRes.data.announcements) {
            setAnnouncements(statusRes.data.announcements);
          }
          
          // Check if competition started
          if (statusRes.data.status === 'countdown') {
            setCompetitionStatus('countdown');
            startCountdown();
          } else if (statusRes.data.status === 'grace_r1') {
            // Pre-Round-1 grace — show a big visible countdown in the lobby.
            // time_remaining_seconds is server-authoritative.
            setCompetitionStatus('grace_r1');
            setGraceLabel(statusRes.data.grace_label || 'Round 1 starts in');
            setGracePaused(!!statusRes.data.is_paused);
            const remaining = statusRes.data.time_remaining_seconds;
            if (typeof remaining === 'number') {
              setGraceSecondsLeft(Math.max(0, Math.floor(remaining)));
            }
          } else if (statusRes.data.status.startsWith('round_')) {
            // Navigate to competition
            navigate(`/competition?participant_id=${participantInfo.participant_id}`);
          }
        } catch (err) {
          console.error('Failed to poll status:', err);
        }
      };
      
      pollStatus();
      interval = setInterval(pollStatus, 2000);
    }
    
    return () => clearInterval(interval);
  }, [joined, participantInfo, navigate]);

  const startCountdown = () => {
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate(`/competition?participant_id=${participantInfo.participant_id}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 1Hz local ticker for the grace_r1 countdown — keeps the displayed seconds
  // smooth between the 2s status polls. The 2s poll re-syncs the value, so any
  // local drift self-corrects.
  useEffect(() => {
    if (competitionStatus !== 'grace_r1' || gracePaused) return undefined;
    if (graceSecondsLeft == null || graceSecondsLeft <= 0) return undefined;
    const t = setTimeout(() => {
      setGraceSecondsLeft(Math.max(0, graceSecondsLeft - 1));
    }, 1000);
    return () => clearTimeout(t);
  }, [competitionStatus, gracePaused, graceSecondsLeft]);

  // Beep at the last 3 seconds (3, 2, 1) using a tiny WebAudio sine tone.
  // No external file needed — works on first user interaction (the kid has
  // already typed an event code to enter the lobby, so audio is unlocked).
  useEffect(() => {
    if (competitionStatus !== 'grace_r1') return;
    if (gracePaused) return;
    if (graceSecondsLeft == null) return;
    if (graceSecondsLeft < 1 || graceSecondsLeft > 3) return;
    // Don't double-fire if the same second value is set twice during a poll+tick race
    if (lastBeepRef.current === graceSecondsLeft) return;
    lastBeepRef.current = graceSecondsLeft;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      // 1 sec → highest pitch (final beep before round starts)
      osc.frequency.value = graceSecondsLeft === 1 ? 880 : 660;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.28);
      osc.onended = () => { try { ctx.close(); } catch (_e) { /* noop */ } };
    } catch (_e) {
      // Audio not supported — silently ignore
    }
  }, [competitionStatus, gracePaused, graceSecondsLeft]);

  const handleJoinLobby = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/competition/join-lobby`, {
        event_code: eventCode.toUpperCase()
      });

      setJoined(true);
      setParticipantInfo(response.data);
      
      // Store in session (event_code enables auto-rejoin on refresh or back-navigation)
      sessionStorage.setItem('participant_id', response.data.participant_id);
      sessionStorage.setItem('participant_name', response.data.child_name);
      sessionStorage.setItem('event_code', eventCode.toUpperCase());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to join lobby');
    } finally {
      setLoading(false);
    }
  };

  const handleStartMockQuiz = () => {
    setMockQuizStarted(true);
    navigate(`/mock-quiz?participant_id=${participantInfo.participant_id}`);
  };

  // Countdown Screen
  if (competitionStatus === 'countdown' && countdown !== null) {
    return (
      <div className="min-h-screen bg-[#2565eb] flex items-center justify-center">
        <div className="text-center animate-fade-in-up">
          <div className="text-9xl font-bold text-white mb-8" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {countdown}
          </div>
          <h2 className="text-3xl font-bold text-white/90">
            Competition Starting...
          </h2>
        </div>
      </div>
    );
  }

  // Pre-Round-1 grace countdown (60s shown live in lobby).
  if (competitionStatus === 'grace_r1' && graceSecondsLeft !== null) {
    const mins = Math.floor(graceSecondsLeft / 60);
    const secs = graceSecondsLeft % 60;
    const display = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : String(secs);
    const isFinalCountdown = graceSecondsLeft <= 3 && graceSecondsLeft > 0;

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a8a] via-[#2565eb] to-[#1e3a8a] flex flex-col items-center justify-center p-6 text-white" data-testid="grace-countdown-screen">
        <div className="flex-1 w-full flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-6 opacity-90">
            <Sparkles className="w-5 h-5 text-[#fbbf24]" />
            <span className="text-sm font-bold tracking-widest uppercase">Event starting soon</span>
            <Sparkles className="w-5 h-5 text-[#fbbf24]" />
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold mb-8" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {graceLabel}
          </h2>

          {/* Big animated number */}
          <div className="relative inline-flex items-center justify-center mb-8">
            <div className={`absolute inset-0 rounded-full ${isFinalCountdown ? 'bg-[#fbbf24]/30' : 'bg-white/10'} animate-ping`} style={{ animationDuration: '1.4s' }} />
            <div className={`relative w-64 h-64 sm:w-72 sm:h-72 rounded-full flex items-center justify-center ${isFinalCountdown ? 'bg-[#fbbf24] text-[#0f172a] shadow-[0_0_60px_rgba(251,191,36,0.6)]' : 'bg-white text-[#1e3a8a]'} transition-colors duration-300`}>
              <span
                className="font-bold leading-none"
                style={{
                  fontFamily: 'Fredoka, sans-serif',
                  fontSize: display.length > 2 ? '6rem' : '9rem',
                }}
                data-testid="grace-countdown-number"
              >
                {display}
              </span>
            </div>
          </div>

          {gracePaused ? (
            <p className="text-base sm:text-lg text-[#fbbf24] font-bold flex items-center gap-2" data-testid="grace-paused-banner">
              <Clock className="w-5 h-5" /> Paused by admin — please wait
            </p>
          ) : (
            <p className="text-base sm:text-lg text-white/85 max-w-md">
              Get ready! Stay on this page — Round 1 will start automatically.
            </p>
          )}

          {/* Other participants in lobby */}
          {lobbyStatus && lobbyStatus.participants && lobbyStatus.participants.length > 0 && (
            <div className="mt-10 max-w-2xl">
              <p className="text-xs uppercase tracking-widest text-white/60 mb-3">
                {lobbyStatus.participants.length} {lobbyStatus.participants.length === 1 ? 'player' : 'players'} ready
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {lobbyStatus.participants.slice(0, 18).map((p, i) => (
                  <span
                    key={p.participant_id || i}
                    className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-sm font-semibold"
                  >
                    {p.username || p.display_name || 'Player'}
                  </span>
                ))}
                {lobbyStatus.participants.length > 18 && (
                  <span className="px-3 py-1 rounded-full bg-white/10 text-sm">+{lobbyStatus.participants.length - 18} more</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Join Lobby Screen (before joining)
  if (!joined) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <BrandHeader />

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#2565eb] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#1e3a8a]">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Join Competition
              </h1>
              <p className="text-[#64748b]">
                Enter your event code to join the lobby
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 text-red-600 text-sm" data-testid="lobby-error">
                {error}
              </div>
            )}

            <div className="card-olympiad p-8">
              <form onSubmit={handleJoinLobby} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#475569] mb-2">
                    Event Code
                  </label>
                  <input
                    type="text"
                    value={eventCode}
                    onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                    className="input-olympiad text-center text-2xl font-mono tracking-widest"
                    placeholder="EVT-XXXXXX"
                    maxLength={12}
                    required
                    data-testid="event-code-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !eventCode}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="join-lobby-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Users className="w-5 h-5" />
                      Join Lobby
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Lobby Screen (after joining)
  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      <header className="p-6 bg-gradient-to-r from-[#2565eb] to-[#1e40af]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div className="text-white">
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Competition Lobby
              </h1>
              <p className="text-white/70 text-sm">Brain Gym Olympiad</p>
            </div>
          </div>
          <div className="text-right text-white">
            <p className="text-sm text-white/70">Welcome,</p>
            <p className="font-bold">{participantInfo?.display_name}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 max-w-4xl mx-auto w-full">
        {/* Status Cards */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="card-olympiad p-6 animate-fade-in-up">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#dcfce7] rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-[#16a34a]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-[#0f172a]">
                  {lobbyStatus?.participant_count || 1}
                </p>
                <p className="text-sm text-[#64748b]">Participants Joined</p>
              </div>
            </div>
          </div>

          <div className="card-olympiad p-6 animate-fade-in-up stagger-1">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                mockQuizCompleted ? 'bg-[#dcfce7]' : 'bg-[#fef3c7]'
              }`}>
                {mockQuizCompleted ? (
                  <CheckCircle className="w-7 h-7 text-[#16a34a]" />
                ) : (
                  <Clock className="w-7 h-7 text-[#d97706]" />
                )}
              </div>
              <div>
                <p className="text-lg font-bold text-[#0f172a]">
                  {mockQuizCompleted ? 'Ready!' : 'Warm Up'}
                </p>
                <p className="text-sm text-[#64748b]">
                  {mockQuizCompleted ? 'Waiting for host' : 'Take the mock quiz'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Practice While You Wait — links to Practice Zone */}
        {participantInfo?.participant_id && (
          <div className="card-olympiad p-8 mb-6 animate-fade-in-up stagger-2" data-testid="lobby-practice-zone-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#eff6ff] rounded-2xl flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-[#2565eb]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Practice While You Wait
                </h2>
                <p className="text-[#64748b]">Sharpen your skills before the event starts</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate(`/practice?pid=${participantInfo.participant_id}&mode=practice`)}
                className="group text-left p-5 rounded-2xl border-2 border-[#e2e8f0] hover:border-[#2565eb] hover:bg-[#eff6ff] transition-all"
                data-testid="lobby-practice-quiz-btn"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Target className="w-6 h-6 text-[#2565eb] group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Practice Quiz</h3>
                </div>
                <p className="text-xs text-[#64748b]">Pick a category and practice at your own pace. No timer.</p>
              </button>

              <button
                onClick={() => navigate(`/practice?pid=${participantInfo.participant_id}&mode=mock`)}
                className="group text-left p-5 rounded-2xl border-2 border-[#e2e8f0] hover:border-[#22c55e] hover:bg-[#f0fdf4] transition-all"
                data-testid="lobby-mock-competition-btn"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Trophy className="w-6 h-6 text-[#22c55e] group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Mock Competition</h3>
                </div>
                <p className="text-xs text-[#64748b]">8 full rounds with timers, just like the real Olympiad.</p>
              </button>
            </div>

            <p className="text-xs text-[#94a3b8] mt-4 text-center">
              You'll stay connected — return to this tab before the host starts the event.
            </p>
          </div>
        )}

        {/* Mock Quiz Section */}
        {!mockQuizCompleted && (
          <div className="card-olympiad p-8 mb-8 animate-fade-in-up stagger-2">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#face39] rounded-2xl flex items-center justify-center shadow-[0_4px_0_#d97706]">
                <Zap className="w-8 h-8 text-[#0f172a]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Warm Up Quiz
                </h2>
                <p className="text-[#64748b]">Practice before the competition starts</p>
              </div>
            </div>

            <div className="bg-[#eff6ff] rounded-2xl p-4 mb-6">
              <ul className="space-y-2 text-sm text-[#475569]">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2565eb]" />
                  10 sample questions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2565eb]" />
                  1 minute time limit
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2565eb]" />
                  Get familiar with the quiz interface
                </li>
              </ul>
            </div>

            <button
              onClick={handleStartMockQuiz}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="start-mock-quiz-button"
            >
              <Play className="w-5 h-5" />
              Start Mock Quiz — 1 Minute
            </button>
          </div>
        )}

        {/* Waiting Screen */}
        {mockQuizCompleted && (
          <div className="card-olympiad p-8 text-center animate-fade-in-up">
            <div className="w-20 h-20 bg-[#2565eb] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#1e3a8a] animate-pulse">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Waiting for host to begin...
            </h2>
            <p className="text-[#64748b] max-w-md mx-auto">
              You're all set! The competition will start when the host begins the event.
              Stay on this page.
            </p>

            <div className="mt-8 p-4 bg-[#fef3c7] rounded-2xl border-2 border-[#f59e0b]">
              <p className="text-sm text-[#92400e]">
                <strong>Tip:</strong> Make sure you have a stable internet connection and avoid refreshing the page.
              </p>
            </div>
          </div>
        )}

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="mt-6 space-y-2 animate-fade-in-up" data-testid="lobby-announcements">
            {announcements.map((a) => (
              <div key={a.id} className="bg-[#fef3c7] rounded-2xl px-5 py-3 border-2 border-[#f59e0b] flex items-start gap-3">
                <span className="text-lg mt-0.5">&#128227;</span>
                <div>
                  <p className="font-semibold text-[#92400e]">{a.message}</p>
                  <p className="text-xs text-[#d97706] mt-1">{new Date(a.sent_at).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Participants List */}
        {lobbyStatus && lobbyStatus.participants && lobbyStatus.participants.length > 0 && (
          <div className="card-olympiad p-6 mt-8 animate-fade-in-up stagger-3">
            <h3 className="font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Participants in Lobby
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {lobbyStatus.participants.map((p, index) => (
                <div 
                  key={p.participant_id}
                  className={`px-4 py-3 rounded-xl text-center text-sm font-medium ${
                    p.participant_id === participantInfo?.participant_id
                      ? 'bg-[#2565eb] text-white'
                      : 'bg-[#f1f5f9] text-[#475569]'
                  }`}
                >
                  {p.display_name}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LobbyPage;
